<template>
  <div class="card modal-overflow" style="box-shadow: none;">
    <div class="card-body">
      <div class="row">
        <div class="col-md-12 text-align-c">
          <h6>Are you sure you want to terminate the following <i><u>PENDING</u></i> Tasks and their associated Job(s)?</h6>
        </div>
      </div>
      <div class="row">
        <el-table class="table-striped wrap-whole-words search-tasks-table indexed-table-column-width"
                  :key="tableKey"
                  id="tasks-table"
                  ref="pendingTasksTable"
                  :data="sortedPendingTableData"
                  border
                  :row-style="isOrphanedTask"
                  :header-cell-class-name="headerSortClass"
                  @selection-change="handleSelectionChange"
                  :max-height="tableHeight(300)"
                  style="width: 100%">
          <el-table-column
            :width="indexColumnWidth"
            align="center"
            label="#"
            type="index" />
          <el-table-column
            align="center"
            type="selection"
            width="60">
          </el-table-column>
          <el-table-column v-for="column in filteredTableColumns"
                           :min-width="column.minWidth"
                           :key="column.label"
                           :label="column.label">
            <template v-slot="props">
              <component :is="column.component" :data="lodash.get(props.row, column.prop)" />
            </template>
          </el-table-column>
          <el-table-column width="1"></el-table-column>
        </el-table>
      </div>
      <div class="row" style="margin-top: 25px;">
        <div class="col-md-12 text-align-c">
          <h6>The following <i><u>BLOCKED</u></i> and <i><u>ON HOLD</u></i> tasks and their associated Job(s) will be terminated</h6>
        </div>
      </div>
      <div class="row">
        <el-table class="table-striped wrap-whole-words search-tasks-table"
                  :key="tableKey"
                  id="tasks-table"
                  ref="blockedAndOnHoldTasksTable"
                  :data="sortedOnHoldOrBlockedTableData"
                  border
                  :row-style="isOrphanedTask"
                  :header-cell-class-name="headerSortClass"
                  @selection-change="handleSelectionChange"
                  :max-height="tableHeight(300)"
                  style="width: 100%">
          <el-table-column
            :width="indexColumnWidth"
            align="center"
            label="#"
            type="index" />
          <el-table-column
            align="center"
            width="60">
          </el-table-column>
          <el-table-column v-for="column in filteredTableColumns"
                           :min-width="column.minWidth"
                           :key="column.label"
                           :label="column.label">
            <template v-slot="props">
              <component :is="column.component" :data="lodash.get(props.row, column.prop)" />
            </template>
          </el-table-column>
          <el-table-column
            width="1"
            class="el-table__fixed"
            fixed="right">
          </el-table-column>
        </el-table>
      </div>
      <div class="row" style="margin-top: 25px;">
        <div class="col-md-12">
          <button type="submit" :disabled="processing" @click.prevent="terminateJobs" class="btn btn-fill btn-info btn-wd pull-right">Terminate</button>
          <button type="cancel" @click="$emit('close')" class="btn btn-wd pull-right">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import {
  assembleTableColumnSortProps,
  filterAndSortTableColumns,
  getTableHeight, notifyError, notifySuccess,
  setIndexColumnWidth, sortNested,
  tableHeaderSortCssClass
} from '@/utilities/utils'
import Constants from '@/utilities/constants'
import TableColumns from '@/utilities/tableColumns'
import lodash from 'lodash'
import {mapGetters} from 'vuex'
import {NO_CONTENT} from 'http-status-codes'
import _ from 'lodash'
import {ptracPatch} from '@/utilities/ptracApi'

export default {
  props: ['data'],
  computed: {
    ...mapGetters({
      user: 'security/user',
    }),
    filteredTableColumns() {
      return filterAndSortTableColumns(this.tableColumns, this.user, Constants.USER_PREFERENCE_TYPES.SEARCH_TASKS)
    },
    indexColumnWidth() {
      return setIndexColumnWidth(this.data.tasks.length)
    },
    sortedPendingTableData() {
      const pendingTasks = this.data.tasks.filter((taskResult) => taskResult._source.status === Constants.STATUSES.PENDING)
      return pendingTasks.sort(sortNested(this.sortedProps))
    },
    onHoldOrBlockedTasks() {
      return this.data.tasks.filter((taskResult) => taskResult._source.status === Constants.STATUSES.ON_HOLD || taskResult._source.status === Constants.STATUSES.BLOCKED)
    },
    sortedOnHoldOrBlockedTableData() {
      return this.onHoldOrBlockedTasks.sort(sortNested(this.sortedProps))
    },
    sortedProps() {
      return assembleTableColumnSortProps(this.filteredTableColumns)
    }
  },
  data() {
    return {
      lodash,
      tableKey: 0,
      tableColumns: TableColumns.TASK_SEARCH,
      selectedPendingTasks: [],
      processing: false
    }
  },
  methods: {
    async terminateJobs() {
      this.processing = true
      // Select selected Pending Tasks to Blocked
      const taskIds = _.map(this.selectedPendingTasks, '_id')

      // for terminate to work below, tasks can't have a pending status
      for (const taskId of taskIds) {
        await ptracPatch(`tasks/${taskId}`, { status: Constants.STATUSES.BLOCKED })
      }

      const selectedJobIds = _.map(this.selectedPendingTasks, '_source.jobId')
      const onHoldOrBlockedJobIds = _.map(this.onHoldOrBlockedTasks, '_source.jobId')
      const jobIds = _.uniq([...selectedJobIds, ...onHoldOrBlockedJobIds])

      const response = await this.$store.dispatch('jobs/terminate', jobIds)

      if (NO_CONTENT === response.status) {
        notifySuccess(this, 'Jobs and tasks terminated successfully')
        this.$store.commit('tasks/search/resetResults')
        await this.data.searchSubmitFromUrl()
        this.$emit('close')
      } else {
        notifyError(this, 'Failed to terminate one or more jobs', response.errors)
      }

      this.processing = false
    },
    selectAll() {
      this.$refs.pendingTasksTable.toggleAllSelection()
    },
    handleSelectionChange(selectedTasks) {
      this.selectedPendingTasks = selectedTasks
    },
    headerSortClass({columnIndex}) {
      return tableHeaderSortCssClass(this.filteredTableColumns, columnIndex, 2)
    },
    isOrphanedTask(props) {
      if (lodash.get(props, 'row._source.orphaned')) {
        return {backgroundColor: '#fbc658'}
      }
    },
    tableHeight(buffer) {
      return getTableHeight(buffer)
    }
  },
  mounted() {
    this.selectAll()
  },
  watch: {
    filteredTableColumns: function() {
      this.tableKey++
    }
  }
}
</script>
